import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { StammkundeService } from '../../../Services/stammkunde.service';
import { CaptureQueueService } from '../../../Services/capture-queue.service';
import { Auftrag, EinsatzMaterial } from '../../../Models/stammkunde.model';

export interface MaterialErfassungData {
  auftrag: Auftrag;
  assignmentId: number;
  /** Titel/Zeit des Einsatzes für die Kopfzeile. */
  einsatzLabel?: string;
  materials: EinsatzMaterial[];
  /** Offline-fähig erfassen (Vorarbeiter im Feld). */
  useQueue?: boolean;
}

/**
 * Dialog, mit dem der Vorarbeiter das an einem Einsatz verwendete Material
 * dokumentiert (ohne Preise). Bei fehlender Verbindung wird offline gespeichert.
 */
@Component({
  selector: 'app-material-erfassung',
  templateUrl: './material-erfassung.component.html',
  styleUrls: ['./material-erfassung.component.css', '../stammkunden-theme.css']
})
export class MaterialErfassungComponent {
  materials: EinsatzMaterial[];
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<MaterialErfassungComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MaterialErfassungData,
    private stammkundeService: StammkundeService,
    private captureQueue: CaptureQueueService,
    private snackBar: MatSnackBar
  ) {
    this.materials = (data.materials ?? []).map(m => ({ ...m }));
  }

  get isOffline(): boolean {
    return !this.captureQueue.isOnline;
  }

  /** Offline-Erfassung aktiv (Feld-Flow ohne Verbindung). */
  get useQueueOffline(): boolean {
    return !!this.data.useQueue && this.isOffline;
  }

  addMaterial(): void {
    this.materials.push({ name: '', quantity: 1, unit: 'Stk' });
  }

  removeMaterial(index: number): void {
    this.materials.splice(index, 1);
  }

  async save(): Promise<void> {
    if (this.saving) { return; }
    this.saving = true;

    const cleaned: EinsatzMaterial[] = this.materials
      .filter(m => m.name.trim())
      .map(m => ({ name: m.name.trim(), quantity: m.quantity || 0, unit: m.unit?.trim() || 'Stk' }));

    try {
      if (this.data.useQueue) {
        const label = `Material · ${this.data.einsatzLabel || this.data.auftrag.title}`;
        const res = await this.captureQueue.captureMaterials(
          this.data.auftrag.id, this.data.assignmentId, cleaned, label);
        this.dialogRef.close({ ok: true, queued: res.queued, materials: cleaned });
        return;
      }

      await firstValueFrom(this.stammkundeService
        .setAssignmentMaterials(this.data.auftrag.id, this.data.assignmentId, cleaned));
      this.dialogRef.close({ ok: true, queued: false, materials: cleaned });
    } catch (err: any) {
      this.saving = false;
      const message = err?.error?.message
        || (typeof err?.error === 'string' && err.error)
        || 'Material konnte nicht gespeichert werden.';
      this.snackBar.open(message, 'OK', { duration: 5000 });
    }
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
